import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon, ReceiptIcon } from 'lucide-react';
import {
  Button,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  Panel,
  SearchInput,
  SkeletonTable,
  StatTile,
  TrendChart,
  cn,
  type DataTableCard,
  type DataTableHeader,
} from '@bcecloud/portal-ui';
import { apiErrorCode, errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { facilitatorPersonPath } from '@/lib/facilitator-spend';
import { salesforceExpenseUrl, salesforceOpportunityUrl, salesforceStatementUrl } from '@/lib/salesforce';
import {
  CONTRIBUTION_NOTE,
  MARGIN_TONE,
  NO_VALUE,
  formatEventDay,
  formatMoney,
  formatMoneyCompact,
  formatPerUnit,
  formatPercent,
  marginTone,
  type MarginTone,
} from '@/lib/program-profitability';
import {
  EVENT_PNL_NOT_FOUND,
  createdLabel,
  eventPnlPath,
  marginVsTargetText,
  toLineRows,
  useEventPnl,
  type EventPnl,
  type EventPnlLine,
  type EventPnlLineRow,
} from '@/lib/event-pnl';

/**
 * One event's Income & Expense Statement: the figures, where the money went
 * by category, and every expense line with links to its Salesforce record
 * and, when the payment came through bcecloud, its receipt. Shared by the
 * Event P&L page and the event's Costs tab.
 */

/** Statement not found: the copy the page and the tab show. */
export const NO_STATEMENT_TITLE = 'No income and expense statement for this event yet';
const NO_STATEMENT_MESSAGE =
  'Salesforce has no statement for this opportunity, or it was created in the last hour and has not copied over yet.';

const LINE_SORT_FIELDS = {
  category: 'category',
  person: 'personName',
  vendor: 'vendorName',
  quantity: 'quantity',
  costEach: 'costEach',
  cost: 'cost',
  shipping: 'shippingCost',
  total: 'total',
  created: 'createdAt',
} as const;
const LINE_SORT_KEYS = Object.values(LINE_SORT_FIELDS);
/** Largest line first: where the money went. */
const LINE_DEFAULT_SORT = { key: LINE_SORT_FIELDS.total, direction: 'desc' } as const;
const LINE_SEARCH_FIELDS = ['category', 'description', 'personName', 'vendorName'] as const satisfies ReadonlyArray<
  keyof EventPnlLineRow
>;

/** The margin tile's tint: red on a loss, amber under target. */
const MARGIN_TILE_CLASS: Readonly<Record<MarginTone, string>> = {
  [MARGIN_TONE.loss]: 'border-rose-300 bg-rose-50',
  [MARGIN_TONE.below]: 'border-amber-300 bg-amber-50',
  [MARGIN_TONE.healthy]: '',
  [MARGIN_TONE.none]: '',
};

const moneyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A line amount: whole dollars, with cents when the amount has them ($11.50). */
function money(value: number | null): string {
  if (value === null) return NO_VALUE;
  return Number.isInteger(value) ? formatMoney(value) : moneyCents.format(value);
}

/** Opens the opportunity and the statement in Salesforce, each in a new tab. */
export function EventPnlSalesforceButtons({ data }: { readonly data: EventPnl }): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" href={salesforceOpportunityUrl(data.header.opportunityId)} target="_blank" rel="noreferrer">
        <ExternalLinkIcon aria-hidden className="size-4" />
        Open opportunity in Salesforce
      </Button>
      <Button variant="secondary" href={salesforceStatementUrl(data.statement.statementId)} target="_blank" rel="noreferrer">
        <ExternalLinkIcon aria-hidden className="size-4" />
        Open statement in Salesforce
      </Button>
    </div>
  );
}

function EventFacts({ data }: { readonly data: EventPnl }): React.ReactElement {
  const { header } = data;
  return (
    <Panel className="mb-4 p-5">
      <FactList
        columns={4}
        phoneColumns={2}
        facts={[
          { label: 'Client', value: header.accountName },
          { label: 'Program', value: header.program },
          { label: 'Event date', value: header.eventDate ? formatEventDay(header.eventDate) : '' },
          { label: 'Participants', value: header.participants === null ? '' : header.participants.toLocaleString('en-US') },
          { label: 'Stage', value: header.stageName },
          { label: 'Contract', value: header.contractNumber },
          { label: 'Sales rep', value: header.ownerName },
          { label: 'Statement', value: data.statement.statementName },
        ]}
      />
    </Panel>
  );
}

function Tiles({ data }: { readonly data: EventPnl }): React.ReactElement {
  const { totals } = data;
  const lineCount = data.categories.reduce((sum, category) => sum + category.lines.length, 0);
  return (
    <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile label="Revenue" value={formatMoney(totals.revenue)} hint={`Commission ${formatMoney(totals.commission)}, iPad fees ${formatMoney(totals.ipadFees)}`} />
      <StatTile label="Itemized cost" value={formatMoney(totals.itemizedExpenses)} hint={`${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`} />
      <StatTile
        label="Contribution"
        value={formatMoney(totals.contribution)}
        hint={totals.contributionPerParticipant === null ? 'No participant count' : `${formatPerUnit(totals.contributionPerParticipant)} per participant`}
      />
      <StatTile
        label="Margin"
        value={formatPercent(totals.margin)}
        hint={marginVsTargetText(totals)}
        className={MARGIN_TILE_CLASS[marginTone(totals.margin, totals.targetMargin)]}
      />
    </div>
  );
}

/** Revenue down to contribution, the way the statement computes it. */
function StatementPanel({ data }: { readonly data: EventPnl }): React.ReactElement {
  const { totals } = data;
  const rows = [
    { label: 'Revenue', value: totals.revenue },
    { label: 'Itemized expenses', value: -totals.itemizedExpenses },
    { label: 'Sales commission', value: -totals.commission },
    { label: 'iPad fees', value: -totals.ipadFees },
  ];
  return (
    <Panel className="p-5">
      <CardHeader title="Income and expense statement" description={CONTRIBUTION_NOTE} />
      <dl className="mt-4 divide-y divide-portal-line">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3 py-2">
            <dt className="text-portal-small text-portal-ink">{row.label}</dt>
            <dd className={cn('text-portal-small tabular-nums', row.value < 0 ? 'text-portal-muted' : 'text-portal-ink')}>{formatMoney(row.value)}</dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 py-2">
          <dt className="text-portal-small font-bold text-portal-ink">Contribution</dt>
          <dd className={cn('text-portal-small font-bold tabular-nums', totals.contribution < 0 ? 'text-portal-danger' : 'text-portal-ink')}>
            {formatMoney(totals.contribution)}
          </dd>
        </div>
      </dl>
    </Panel>
  );
}

function CategoryPanel({ data }: { readonly data: EventPnl }): React.ReactElement {
  return (
    <Panel className="p-5">
      <CardHeader title="Cost by category" description="What the expense lines add up to in each category. Hover a bar for its share." />
      <TrendChart
        title="Cost by category"
        unit="Cost"
        points={data.categories.map((category) => ({
          key: category.category,
          label: category.category,
          value: category.subtotal,
          details: [
            `${formatPercent(category.share)} of line cost`,
            `${category.lines.length} ${category.lines.length === 1 ? 'line' : 'lines'}`,
          ],
        }))}
        formatValue={formatMoneyCompact}
        height={260}
      />
    </Panel>
  );
}

function shippingDetail(line: EventPnlLine): string {
  return [line.shippingMethod, line.trackingNumber].filter(Boolean).join(' · ');
}

function LineLinks({ line }: { readonly line: EventPnlLine }): React.ReactElement {
  const receiptUrl = line.receipt?.receiptUrl ?? null;
  return (
    <span className="flex flex-col gap-0.5">
      <a
        href={salesforceExpenseUrl(line.expenseId)}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-brand hover:underline"
        aria-label={`Open the ${line.category} line in Salesforce`}
      >
        <ExternalLinkIcon aria-hidden className="size-3.5" />
        Salesforce
      </a>
      {receiptUrl ? (
        <a href={receiptUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
          <ReceiptIcon aria-hidden className="size-3.5" />
          Receipt
        </a>
      ) : line.receipt ? (
        <span className="text-portal-micro text-portal-muted">No receipt for this line</span>
      ) : null}
    </span>
  );
}

function PersonCell({ row }: { readonly row: EventPnlLineRow }): React.ReactElement {
  if (!row.contactId) return <span className="text-portal-muted">{NO_VALUE}</span>;
  return (
    <Link to={facilitatorPersonPath(row.contactId)} className="font-bold text-brand hover:underline">
      {row.personName || 'Unnamed contact'}
    </Link>
  );
}

function LinesTable({ data }: { readonly data: EventPnl }): React.ReactElement {
  const { sort, onSort } = useTableSort(LINE_SORT_KEYS, LINE_DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const allRows = useMemo(() => toLineRows(data.categories), [data.categories]);
  const rows = useMemo(() => sortRows(filterRows(allRows, search, LINE_SEARCH_FIELDS), sort), [allRows, search, sort]);

  if (allRows.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<ReceiptIcon aria-hidden />}
          title="No expense lines on this statement"
          message="Facilitator payments, travel, supplies and shipping show here once they are added to the statement in Salesforce."
          action={
            <Button variant="secondary" href={salesforceStatementUrl(data.statement.statementId)} target="_blank" rel="noreferrer">
              Open statement in Salesforce
            </Button>
          }
        />
      </Panel>
    );
  }

  const headers: DataTableHeader[] = [
    { label: 'Line', sortKey: LINE_SORT_FIELDS.category },
    { label: 'Person', sortKey: LINE_SORT_FIELDS.person },
    { label: 'Vendor', sortKey: LINE_SORT_FIELDS.vendor },
    { label: 'Qty', sortKey: LINE_SORT_FIELDS.quantity },
    { label: 'Cost each', sortKey: LINE_SORT_FIELDS.costEach },
    { label: 'Cost', sortKey: LINE_SORT_FIELDS.cost },
    { label: 'Shipping', sortKey: LINE_SORT_FIELDS.shipping },
    { label: 'Total', sortKey: LINE_SORT_FIELDS.total },
    { label: 'Added', sortKey: LINE_SORT_FIELDS.created },
    'Records',
  ];
  const cards: DataTableCard[] = rows.map((row) => ({
    key: row.expenseId,
    title: row.category,
    subtitle: [row.description, row.personName, row.vendorName].filter(Boolean).join(' · '),
    fields: [
      { label: 'Qty', value: row.quantity ?? NO_VALUE },
      { label: 'Cost each', value: money(row.costEach) },
      { label: 'Shipping', value: money(row.shippingCost) },
      { label: 'Total', value: money(row.total) },
    ],
    action: <LineLinks line={row.line} />,
  }));

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search expense lines"
          placeholder="Category, description, person or vendor"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <span className="text-portal-micro text-portal-muted">
          {rows.length} {rows.length === 1 ? 'line' : 'lines'}
        </span>
      </div>
      {rows.length === 0 ? (
        <Panel>
          <EmptyState title="No lines match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable headers={headers} rightAlignedHeaders={[3, 4, 5, 6, 7]} sort={sort} onSort={onSort} minWidth={1240} cards={cards}>
          {rows.map((row) => {
            const description = row.description.trim();
            const shipping = shippingDetail(row.line);
            return (
              <tr key={row.expenseId} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <span className="block font-bold text-portal-ink">{row.category}</span>
                  {description && description.toLowerCase() !== row.category.toLowerCase() ? (
                    <span className="block text-portal-micro text-portal-muted">{description}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small">
                  <PersonCell row={row} />
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{row.vendorName || NO_VALUE}</td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{row.quantity ?? NO_VALUE}</td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{money(row.costEach)}</td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{money(row.cost)}</td>
                <td className="p-3 text-right text-portal-small tabular-nums">
                  <span className="block whitespace-nowrap">{money(row.shippingCost)}</span>
                  {shipping ? <span className="block text-portal-micro text-portal-muted">{shipping}</span> : null}
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">{money(row.total)}</td>
                <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{createdLabel(row.createdAt)}</td>
                <td className="whitespace-nowrap p-3 text-portal-small">
                  <LineLinks line={row.line} />
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}
    </>
  );
}

/** The statement's figures, the category breakdown and the line table. */
export function EventPnlReport({ data }: { readonly data: EventPnl }): React.ReactElement {
  const { totals } = data;
  const hasLines = data.categories.length > 0;
  return (
    <>
      <EventFacts data={data} />
      <Tiles data={data} />
      {totals.unexplained !== 0 ? (
        <ErrorNotice
          inline
          tone="warning"
          message={
            totals.unexplained > 0
              ? `The statement's itemized expenses are ${formatMoney(totals.unexplained)} more than its lines add up to (${formatMoney(totals.linesTotal)}).`
              : `The lines add up to ${formatMoney(totals.linesTotal)}, ${formatMoney(-totals.unexplained)} more than the statement's itemized expenses.`
          }
        />
      ) : null}
      <div className={cn('mb-4 grid gap-4', hasLines && 'lg:grid-cols-2')}>
        <StatementPanel data={data} />
        {hasLines ? <CategoryPanel data={data} /> : null}
      </div>
      <LinesTable data={data} />
    </>
  );
}

/** Loading, the 404 and other failures, around whatever renders the P&L. */
export function EventPnlStates({
  query,
  children,
}: {
  readonly query: ReturnType<typeof useEventPnl>;
  readonly children: (data: EventPnl) => React.ReactNode;
}): React.ReactElement {
  if (query.isError) {
    return apiErrorCode(query.error) === EVENT_PNL_NOT_FOUND ? (
      <ErrorNotice title={NO_STATEMENT_TITLE} message={NO_STATEMENT_MESSAGE} />
    ) : (
      <ErrorNotice title="This event's P&L is unavailable" message={errorMessage(query.error)} />
    );
  }
  if (query.isPending) return <SkeletonTable label="Loading the statement…" />;
  return <>{children(query.data)}</>;
}

/**
 * The event's Costs tab: the same P&L, with the Salesforce buttons and a
 * link to the full page. Render it only for view_accounting holders.
 */
export function EventCostsTab({ opportunityId }: { readonly opportunityId: string }): React.ReactElement {
  const query = useEventPnl(opportunityId);
  return (
    <div className="mt-4">
      <EventPnlStates query={query}>
        {(data) => (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <EventPnlSalesforceButtons data={data} />
              <Link to={eventPnlPath(opportunityId)} className="text-portal-small font-bold text-brand hover:underline">
                Open the full Event P&L
              </Link>
            </div>
            <EventPnlReport data={data} />
          </>
        )}
      </EventPnlStates>
    </div>
  );
}

/**
 * An event's name linking to its Event P&L, with a small icon link that
 * opens the opportunity in Salesforce. For event cells in accounting tables.
 */
export function EventPnlNameLink({
  opportunityId,
  name,
  bold = false,
  showSalesforce = true,
}: {
  readonly opportunityId: string;
  readonly name: string;
  readonly bold?: boolean;
  /** False where the row already has its own Salesforce links. */
  readonly showSalesforce?: boolean;
}): React.ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      <Link to={eventPnlPath(opportunityId)} className={cn('text-brand hover:underline', bold && 'font-bold')}>
        {name}
      </Link>
      {showSalesforce ? (
        <a
          href={salesforceOpportunityUrl(opportunityId)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-portal-muted hover:text-brand"
          aria-label={`Open ${name} in Salesforce`}
          title="Open in Salesforce"
        >
          <ExternalLinkIcon aria-hidden className="size-3.5" />
        </a>
      ) : null}
    </span>
  );
}
